'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface LoginFormData {
  username: string;
  password: string;
  remember: boolean;
}

const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export default function LoginForm() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { username: '', password: '', remember: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    const username = data.username.trim();

    try {
      let emailToUse: string;

      if (isEmail(username)) {
        // Admin path — username is already an email
        emailToUse = username;
      } else {
        // Viewer path — look up email by username field
        const supabase = createClient();
        const { data: profiles, error: lookupError } = await supabase
          .from('user_profiles')
          .select('email, role')
          .eq('username', username)
          .eq('role', 'Viewer')
          .limit(1);

        if (lookupError || !profiles || profiles.length === 0) {
          toast.error('No account found with that username. Please check and try again.');
          return;
        }

        emailToUse = profiles[0].email;
      }

      const result = await signIn(emailToUse, data.password);
      toast.success('Signed in successfully');

      // Fetch role to redirect appropriately
      const supabase = createClient();
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', result.user?.id)
        .single();

      const role = profile?.role;
      const destination = role === 'Administrator' ? '/' : '/meter-search';
      router.push(destination);
      router.refresh();
    } catch (err: any) {
      toast.error(err?.message || 'Invalid credentials. Please check your username and password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-2/5 bg-primary p-12 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={`circle-${i}`}
              className="absolute rounded-full border border-white"
              style={{
                width: `${(i + 1) * 80}px`,
                height: `${(i + 1) * 80}px`,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          ))}
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <AppLogo size={40} />
            <span className="text-white font-bold text-xl">MeterLookup</span>
          </div>
        </div>

        <div className="relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
            <Icon name="SignalIcon" size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4 leading-tight">
            Utility Meter Registry & Customer Information System
          </h1>
          <p className="text-white/70 text-base leading-relaxed">
            Instant meter number lookup for field officers. Manage thousands of records with
            confidence.
          </p>
        </div>

        <div className="relative z-10 flex items-center gap-6">
          {[
            { value: '124K+', label: 'Meters Indexed' },
            { value: '99.8%', label: 'Uptime' },
            { value: '0.3s', label: 'Avg Search' },
          ].map((s) => (
            <div key={`stat-${s.label}`}>
              <p className="text-white font-bold text-xl font-tabular">{s.value}</p>
              <p className="text-white/60 text-xs">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-bold text-lg text-foreground">MeterLookup</span>
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-1">Welcome back</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Sign in to your account to continue
          </p>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
            {/* Username field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-foreground mb-1">
                Username
              </label>
              <input
                id="username"
                type="text"
                autoComplete="username"
                className={`input-field ${errors.username ? 'border-negative focus:border-negative focus:ring-negative/20' : ''}`}
                placeholder="Enter your name or email address"
                {...register('username', {
                  required: 'Username is required',
                  minLength: { value: 2, message: 'Username must be at least 2 characters' },
                })}
              />
              {errors.username && (
                <p className="mt-1 text-xs text-negative">{errors.username.message}</p>
              )}
            </div>

            {/* Password field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className={`input-field pr-10 ${errors.password ? 'border-negative focus:border-negative focus:ring-negative/20' : ''}`}
                  placeholder="Enter your password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-negative">{errors.password.message}</p>
              )}
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2">
              <input
                id="remember"
                type="checkbox"
                className="w-4 h-4 rounded border-border accent-primary"
                {...register('remember')}
              />
              <label htmlFor="remember" className="text-sm text-foreground">
                Keep me signed in for 30 days
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Icon name="ArrowPathIcon" size={15} className="animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <Icon name="ArrowRightOnRectangleIcon" size={15} />
                  Sign In
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}